import { useEffect, useState } from "react";
import CustomButton from "../../../components/Form/ArrowButton";
import CustomDropDown from "../../../components/Form/Dropdown";
import { useNavigate } from "react-router-dom";
import ProgressBar from "../../../components/Form/ProgressBar";
import { useSelector, useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import useForm from "../../../hooks/useForm";
import { USER_INPUT_FIELD_TYPES } from "../../../constants/userInputFieldNames";
import LoginInput from "../../../components/Form/LoginInput";
import { setFormDataFiles, setFormDataGlobState, setPendingPaymentOrderId } from "../../../store";
import { REERENCE_STYLES, SUBJECTS_ARRAY } from "../../../Data/orderProperties";
import UploadIcon from "../../../assets/Icons/upload.svg";
import showToast from "../../../utils/toastNotifications";
import { MAX_FILE_SIZE_MB, ORDER_TOPIC_MAX_LENGTH, referencesFormats, tooltips } from "../../../constants/commonConstants";
import CustomFileIcon from "../../../components/CustomFileIcon";
import { Icon } from "@iconify/react";
import OrderSummarySidebar from "../../../components/Dashboard/OrderSummarySidebar";

const getValidatedOrderFormData = (data) => {
  const fieldNames = [
    "subject",
    "topic",
    "orderInstructions",
    "referenceStyle",
    "writingType",
    "typeOfPaper",
    "academicLevel",
    "typeOfEnglish",
    "deadline",
    "spacing",
    "previousOrderId",
    "aditionalServices",
    "requestedWriterId",
    "numberOfFigures",
    "pages",
    "slides",
    "needDocumentation",
    "needPresentation",
    "budgetIncrement",
  ];

  return fieldNames.reduce((formData, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(data || {}, fieldName)) {
      formData[fieldName] = data[fieldName];
    }

    return formData;
  }, {});
};

const PaperInstructions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createOrder = useSelector((state) => state.createOrder);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      ...getValidatedOrderFormData(createOrder.formData),
      subject: "",
      topic: "Writer's choice",
      orderInstructions: "",
      referenceStyle: "APA",
    },
    {
      subject: true,
      topic: false,
      orderInstructions: true,
      referenceStyle: true,
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      pages: false,
      slides: false,
      needDocumentation: false,
      needPresentation: false,
      budgetIncrement: true
    },
    {
      subject: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      topic: USER_INPUT_FIELD_TYPES.TOPIC,
      orderInstructions: USER_INPUT_FIELD_TYPES.MIN_30_CHARACTORS,
      referenceStyle: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      writingType: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfPaper: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      academicLevel: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfEnglish: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      deadline: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      spacing: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      previousOrderId: USER_INPUT_FIELD_TYPES.ORDER_ID,
      aditionalServices: USER_INPUT_FIELD_TYPES.ADITIONAL_SERVICES,
      requestedWriterId: USER_INPUT_FIELD_TYPES.WRITER_ID,
      numberOfFigures: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      pages: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      slides: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      needDocumentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      needPresentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      budgetIncrement: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  // Progressbar functions
  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Additional Services", shortLabel: "Services" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  // State to store user reference files
  const [uploadFiles, setUploadFiles] = useState([]);

  // naviagating back if the previous necessary fields are empty
  useEffect(() => {
    if(
      !(createOrder?.formData?.typeOfPaper) ||
      !(createOrder?.formData?.academicLevel) ||
      !(createOrder?.formData?.deadline) ||
      !(createOrder?.formData?.spacing) ||
      !(createOrder?.formData?.writingType) || 
      !(createOrder?.formData?.typeOfEnglish)  
    ){
      navigate("/calculation");
    }
  }, []);

  useEffect(() => {
    if (
      createOrder &&
      createOrder.formData &&
      Object.keys(createOrder.formData)?.length > 0
    ) {
      setFormData({
        ...getValidatedOrderFormData(formData),
        ...getValidatedOrderFormData(createOrder.formData),
        subject: createOrder.formData?.subject || "",
        topic: createOrder.formData?.topic || "Writer's choice",
        orderInstructions: createOrder.formData?.orderInstructions || "",
        referenceStyle: createOrder.formData?.referenceStyle || "APA",
      });
      setUploadFiles(createOrder?.formDataFiles || []);
    }
  }, [createOrder]);

  const getFilesComparableData = (files) => {
    return (files || []).map((file) => ({
      name: file?.name || file,
      size: file?.size || null,
      lastModified: file?.lastModified || null,
    }));
  };

  const hasDraftChangedSincePendingOrder = () => {
    const formChanged = JSON.stringify(getValidatedOrderFormData(formData)) !==
      JSON.stringify(getValidatedOrderFormData(createOrder.formData));
    const filesChanged = JSON.stringify(getFilesComparableData(uploadFiles)) !==
      JSON.stringify(getFilesComparableData(createOrder.formDataFiles));

    return formChanged || filesChanged;
  };

  const handleBack = () => {
    dispatch(setFormDataGlobState({
      ...getValidatedOrderFormData(createOrder.formData),
      ...getValidatedOrderFormData(formData)
    }));
    dispatch(setFormDataFiles(uploadFiles || []))
    navigate("/calculation");
  };

  const handleNext = async () => {
    // Mark all fields as touched
    setTouched({
      subject: true,
      topic: true,
      orderInstructions: true,
      referenceStyle: true,
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      pages: true,
      slides: true,
      needDocumentation: true,
      needPresentation: true,
      budgetIncrement: true
    });

    console.log("Form Data:", formData);
    console.log("errors:", errors);

    if (!validateForm()) {
      console.log("Validation failed");
      showToast("error", "Please fill all required fields");
      return;
    }
    const orderFormData = getValidatedOrderFormData(formData);
    if (createOrder.pendingPaymentOrderId && hasDraftChangedSincePendingOrder()) {
      dispatch(setPendingPaymentOrderId(""));
    }

    dispatch(
      setFormDataGlobState({ ...orderFormData })
    );
    dispatch(setFormDataFiles(uploadFiles || []));
    navigate("/additional-services");
  };

  //Drag Upload
  const { getRootProps, getInputProps } = useDropzone({
    accept: referencesFormats,
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024,
    onDrop: (acceptedFiles) => {
      setUploadFiles((prev) => [...prev, ...acceptedFiles]);
    },
    onDropRejected: (rejectedFiles) => {
      const exceedsSizeLimit = rejectedFiles.some(({ errors: fileErrors }) =>
        fileErrors.some(({ code }) => code === "file-too-large")
      );

      showToast(
        "error",
        exceedsSizeLimit
          ? `Each file must be ${MAX_FILE_SIZE_MB} MB or smaller.`
          : "This file type is not supported. Please upload a PDF, document, spreadsheet, PowerPoint, image, text, ZIP, or RAR file."
      );
    },
  });

  // console.log('createOrder peper instructuion', createOrder.formDataFiles)
  const summaryPages = Number(formData.pages || 0);
  const summarySlides = Number(formData.slides || 0);

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      {/* header section */}
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className=" block xl:hidden mr-2">Dashboard </h2>
      </div>

      {/* scrollable section */}
      <div className="overflow-y-auto xl:mt-10">
        <div className="bg-white  mt-[32px] ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px]   md:py-10  max-w-full md:max-w-[2200px] w-auto mx-auto">
          <ProgressBar steps={steps} activeStep={2} completedStep={1} />{" "}
          <div className="grid w-full grid-cols-1 items-start gap-6 1600-br:grid-cols-[minmax(0,1fr)_280px]">
          <div className="flex flex-col justify-center items-center mt-[-80px] w-full">
            <div
              className="grid
      grid-cols-1
      gap-x-6 mt-10 w-full
      justify-center items-center
    "
            >
              <div className="mt-20">
                <CustomDropDown
                  label="Subject"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.subject && errors.subject}
                  defaultOption="Select a Subject"
                  idKey="id"
                  valueKey="name"
                  options={SUBJECTS_ARRAY}
                  dropdownDivStyle="w-full max-h-[240px] max-h-[166px]"
                  searchable
                  searchPlaceholder="Start typing a subject"
                  tooltip={tooltips.SUBJECT}
                />
              </div>

              <div className="mt-0">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="topic"
                  name="topic"
                  label="Topic"
                  type="text"
                  value={formData.topic}
                  placeholder="Enter The Topic"
                  maxLength={ORDER_TOPIC_MAX_LENGTH}
                  error={touched.topic && errors.topic}
                  onChange={(e) => handleInputChange("topic", e.target.value)}
                  onBlur={() => handleBlur("topic", formData.topic)}
                  isRequired={false}
                  isFloatingLabel={false}
                  tooltip={tooltips.TOPIC}
                />
              </div>

              <div className="">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="orderInstructions"
                  name="orderInstructions"
                  label="Order Instructions"
                  type="textarea"
                  allowHtml
                  value={formData.orderInstructions}
                  placeholder="Minimum 30 characters and maximum 5000 characters."
                  maxLength={5000}
                  error={touched.orderInstructions && errors.orderInstructions}
                  onChange={(e) =>
                    handleInputChange("orderInstructions", e.target.value)
                  }
                  onBlur={(e) =>
                    handleBlur("orderInstructions", e.target.value)
                  }
                  isFloatingLabel={false}
                  inputStyle="w-full min-h-[104px] max-h-[240px] text-left placeholder:whitespace-normal sm:placeholder:text-sm"
                  tooltip={tooltips.INSTRUCTIONS}
                />
              </div>

              <div className="">
                <CustomDropDown
                  label="Reference Style"
                  id="reference-style"
                  name="referenceStyle"
                  value={formData.referenceStyle}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.referenceStyle && errors.referenceStyle}
                  defaultOption="APA"
                  idKey="id"
                  valueKey="name"
                  options={REERENCE_STYLES}
                  dropdownDivStyle="w-full max-h-[240px] sm:w-[240px] md:w-[240px] lg:w-[320px] max-h-[166px]"
                  tooltip={tooltips.CITATION_STYLE}
                />
              </div>

              <div className="w-full">
                <div className="flex gap-4">
                  <span className="lg:text-level-3 md:text-level-3 text-secondary font-medium leading-6">
                    Additional Documents/ Reference Materials
                  </span>
                  <div className="relative group cursor-pointer">
                    <Icon
                      icon="mdi:help-circle-outline"
                      className="text-[#6B7280]"
                      width={18}
                      height={18}
                    />
                    <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                    rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                    min-w-[300px] break-words text-center whitespace-normal">
                      {tooltips.UPLOADS}
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex flex-wrap  gap-2 lg:gap-4 m-2 lg:m-4">
                    {
                      uploadFiles.map((file, index) => {
                        return <CustomFileIcon 
                                  name={file.name} 
                                  extension={file.name.split('.').pop().toLowerCase()}
                                  downloadable={false}
                                  removeFile={() => setUploadFiles(prev => prev.filter((f, i) => i !== index))}
                                />
                      })
                    }
                  </div>
                </div>
                <div
                  {...getRootProps()}
                  className="border-dashed mt-3 border-2 border-blue-shades rounded p-4 h-[192px] flex flex-col items-center justify-center cursor-pointer"
                >
                  <input {...getInputProps()} className="hidden" />
                  <div className="">
                      <div className=" relative">
                        <img
                          src={UploadIcon}
                          className="w-[51px] h-[43px]"
                          alt="Upload"
                        />
                        <span className=" w-[22px] h-[22px] rounded-full bg-blue-400 text-[14px] font-inter text-white flex items-center justify-center absolute -top-1 -right-1">
                          {uploadFiles?.length ||
                            createOrder.formData?.referenceDocuments?.length ||
                            0}
                        </span>
                      </div>
                  </div>
                  <p className="mb-2 text-center text-secondary font-inter leading-5 mt-6 text-level-4">
                    Drag your file(s) to start uploading{" "} - Maximum size of a file is 20MB
                  </p>
                  <p className="text-gray text-level-5  mb-[8px]">OR</p>
                  <p className="border-[1px] border-blue-shades rounded-[8px] py-[6px] px-3 text-level-5 text-blue-shades font-semibold leading-[18px] ">
                    Browse files
                  </p>
                  {/* <p>
                    Maximum size of a file is 20MB
                  </p> */}
                </div>
              </div>

              <div className="flex justify-between gap-1.5 mt-[16px] w-full">
                <CustomButton
                  text="Back"
                  iconDirection="left"
                  iconPosition="before"
                  onClick={handleBack}
                  width="clamp(76px, 24vw, 104px)"
                  height="44px"
                  buttonClassName="gap-2 px-2 text-[12px] sm:px-3"
                  iconClassName="h-6 w-6 shrink-0"
                />
                <CustomButton
                  text={
                    <>
                      <span className="sm:hidden">Next: Services</span>
                      <span className="hidden sm:inline">Next: Additional Services</span>
                    </>
                  }
                  type="submit"
                  iconDirection="right"
                  iconPosition="after"
                  onClick={handleNext}
                  width="clamp(128px, 46vw, 190px)"
                  height="44px"
                  buttonClassName="gap-1 px-1.5 text-[11px] leading-none hover:bg-black disabled:hover:bg-slate-300 sm:gap-2 sm:px-3 sm:text-[12px]"
                  iconClassName="h-3 w-3 sm:h-4 sm:w-4"
                />
              </div>
            </div>
          </div>
          <OrderSummarySidebar
            formData={formData}
            pages={summaryPages}
            slides={summarySlides}
            totalUnits={summaryPages + summarySlides}
            subject={formData.subject}
          />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperInstructions;
